import React, { useState, useEffect } from 'react'
import { Button } from "../../../../components/common/Buttons"
import { ModalDialog } from "../../../../components/common/Dialogs"
import {
  InlineContainer,
  SpacingContainer,
} from "../../../../components/common/Containers/Base"
import { useTrans } from "../../../../services/i18n"
import Typography from "../../../../components/common/Typography"
import { Paper } from "../../../../components/common/Containers"
import { Grid, Box, TextField } from "@material-ui/core"
import { intlDateFormat } from '../../../../utils/dateUtils'
import connect from '../connect'
import Loader from '../../../../components/common/Loader'

const ReplyModal = ({
  open,
  onClose,
  form,
  subcontractorFormReplies,
  repliesPending,
  getReplies,
  createReply,
  fetchList,
  authUser,
  batchUpdateStatus
}) => {
  const { trans } = useTrans()
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)

  useEffect(() => {
    if (open && form?.id) {
      getReplies({ organizationFormId: form.id })
    }
  }, [open, form?.id, getReplies])

  const handleSendReply = async () => {
    if (!message.trim()) return

    setSending(true)
    try {
      await createReply({
        organizationFormId: form.id,
        message: message.trim(),
        recipientOrgId: form.organizationId,
        senderId: authUser?.id
      })

      // Update admin status to 'progress' after sending reply
      await batchUpdateStatus({
        forms: [{
          organizationFormId: form.id,
          adminStatus: 'progress'
        }]
      })

      setMessage('')
      // Refresh the replies list
      getReplies({ organizationFormId: form.id })
      // Optionally refresh the main list
      fetchList()
    } catch (error) {
      console.error('Error sending reply:', error)
    } finally {
      setSending(false)
    }
  }

  const handleClose = () => {
    setMessage('')
    onClose()
  }

  return (
    <ModalDialog
      open={open}
      onClose={handleClose}
      disableContentSpacing
      title={`${trans("form-replies")} - ${form?.organizationName || ''}`}
      maxWidth="md"
      fullWidth
      actions={
        <InlineContainer>
          <Button
            onClick={handleClose}
            variant="outlined"
            color="primary"
          >
            {trans("close")}
          </Button>
        </InlineContainer>
      }
    >
      <SpacingContainer px={4} py={4}>
        <Typography
          variant="h5"
          weight={600}
          fontFamily="lexend"
          gutter="bottom2"
        >
          {trans("message-history")}
        </Typography>

        {/* Message History */}
        <Box style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '20px' }}>
          {repliesPending ? (
            <Loader open />
          ) : subcontractorFormReplies && subcontractorFormReplies.length > 0 ? (
            subcontractorFormReplies.map((reply, index) => (
              <Paper
                key={reply.id || index}
                component={SpacingContainer}
                py={2}
                px="4sm3"
                bgColor="paperSecondary"
                radius={4}
                style={{ marginBottom: '12px' }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        <Typography
                          variant="body2"
                          weight={600}
                          color="primary"
                        >
                          {trans("from")}: {reply.senderName || trans("administrator")}
                        </Typography>
                        <Typography
                          variant="body2"
                          weight={600}
                          color="secondary"
                        >
                          {trans("to")}: {reply.recipientOrgName || form?.organizationName}
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                      >
                        {intlDateFormat(reply.createdAt, 'en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric' })}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body1">
                      {reply.message}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            ))
          ) : (
            <Typography variant="body2" color="textSecondary">
              {trans("no-messages-yet")}
            </Typography>
          )}
        </Box>

        {/* Send New Message */}
        <Typography
          variant="h6"
          weight={600}
          fontFamily="lexend"
          gutter="bottom1"
        >
          {trans("send-new-message")}
        </Typography>
        
        <Box>
          <TextField
            multiline
            rows={4}
            variant="outlined"
            fullWidth
            placeholder={trans("type-your-message")}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            style={{ marginBottom: '16px' }}
          />
          
          <Box display="flex" justifyContent="flex-end">
            <Button
              onClick={handleSendReply}
              variant="contained"
              color="primary"
              disabled={!message.trim() || sending}
            >
              {sending ? trans("sending") : trans("send-message")}
            </Button>
          </Box>
        </Box>
      </SpacingContainer>
    </ModalDialog>
  )
}

export default connect(ReplyModal)